/**
 * SurveyTemplatesPage — HR question builder for Employee Survey templates.
 *
 * Lists all survey templates. HR can create/edit/delete a template, adding
 * questions of 5 types: Rating, Multiple Choice, Checkbox, Yes/No, and Text.
 * Choice-based questions (Multiple Choice / Checkbox) require at least two options.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

import PageTitle from "../../../components/Page Component/PageTitle";
import { deleteData, fetchData, postData, putData } from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { surveyTemplatesUrl, surveyTemplateDetailUrl } from "../../../Service/Apis";
import { stripHtml, sanitizeBoldHtml } from "../../../utils/richTextUtils";

// ── Types ────────────────────────────────────────────────────────────────────

type QuestionType = "rating" | "multiple_choice" | "checkbox" | "yes_no" | "text";
type RatingStyle = "agreement" | "happiness" | "frequency";

const NON_RATING_TYPE_LABELS: Record<Exclude<QuestionType, "rating">, string> = {
  multiple_choice: "Single Choice",
  checkbox: "Checkbox",
  yes_no: "Yes / No",
  text: "Text",
};

const NON_RATING_TYPES: Array<Exclude<QuestionType, "rating">> = [
  "multiple_choice",
  "checkbox",
  "yes_no",
  "text",
];

/**
 * A "rating" question is really scale + anchor-label set. Each variant below
 * is offered as its own entry in the Type dropdown (so HR picks e.g. "Rating
 * (1-3 Happiness)" directly) but maps down to the same underlying
 * question_type="rating" plus a (rating_scale, rating_style) pair. `helper`
 * mirrors the anchor labels shown to employees on the survey form.
 */
interface RatingVariant {
  key: string;
  scale: number;
  style: RatingStyle;
  label: string;
  helper: string;
}

const RATING_VARIANTS: RatingVariant[] = [
  {
    key: "rating_agreement_5",
    scale: 5,
    style: "agreement",
    label: "Rating (1-5 Agreement)",
    helper: "1 = Strongly Disagree · 2 = Disagree · 3 = Neutral · 4 = Agree · 5 = Strongly Agree",
  },
  {
    key: "rating_agreement_3",
    scale: 3,
    style: "agreement",
    label: "Rating (1-3 Agreement)",
    helper: "1 = Disagree · 2 = Neutral · 3 = Agree",
  },
  {
    key: "rating_happiness_3",
    scale: 3,
    style: "happiness",
    label: "Rating (1-3 Happiness)",
    helper: "1 = Not Happy · 2 = Neutral · 3 = Happy",
  },
  {
    key: "rating_frequency_3",
    scale: 3,
    style: "frequency",
    label: "Rating (1-3 Frequency)",
    helper: "1 = No · 2 = Sometimes · 3 = Yes",
  },
];

/** Looks up the rating variant matching a given (scale, style) pair, if any. */
const findRatingVariant = (scale: number | null, style: string | null): RatingVariant | undefined =>
  RATING_VARIANTS.find((v) => v.scale === scale && v.style === style);

/** Computes the Type <Select>'s current value for a question row: a rating variant key, or the plain question_type. */
const typeSelectValue = (q: Pick<QuestionForm, "question_type" | "rating_scale" | "rating_style">): string =>
  q.question_type === "rating"
    ? findRatingVariant(q.rating_scale, q.rating_style)?.key ?? RATING_VARIANTS[0].key
    : q.question_type;

interface OptionForm {
  option_id?: string;
  label: string;
  order: number;
}

interface QuestionForm {
  question_id?: string;
  question_text: string;
  question_type: QuestionType;
  rating_scale: number | null;
  rating_style: RatingStyle | null;
  is_mandatory: boolean;
  order: number;
  options: OptionForm[];
}

interface TemplateListItem {
  template_id: string;
  name: string;
  is_active: boolean;
  questions_count: number;
  active_cycle_count: number;
  created_by_name: string;
  created_at: string;
  questions_read: Array<{
    question_id: string;
    question_text: string;
    question_type: QuestionType;
    rating_scale: number | null;
    rating_style: RatingStyle | null;
    is_mandatory: boolean;
    order: number;
    options: OptionForm[];
  }>;
}

/** A fresh, blank question row for the builder form, at the given display order. */
const emptyQuestion = (order: number): QuestionForm => ({
  question_text: "",
  question_type: "text",
  rating_scale: null,
  rating_style: null,
  is_mandatory: true,
  order,
  options: [],
});

/** A fresh, blank option row for a Single Choice / Checkbox question. */
const emptyOption = (order: number): OptionForm => ({ label: "", order });

/** Whether a question type requires an `options` list (Single Choice, Checkbox). */
const needsOptions = (t: QuestionType) => t === "multiple_choice" || t === "checkbox";

/** Tiptap extension set for the question text editor: only bold formatting, no lists/headings/etc. */
const questionEditorExtensions = [
  StarterKit.configure({
    italic: false,
    strike: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    heading: false,
    horizontalRule: false,
  }),
];

interface QuestionTextEditorProps {
  label: string;
  value: string;
  onChange: (html: string) => void;
}

/**
 * Rich text input for a question's text, supporting **bold only** (via the
 * toolbar button, Ctrl+B, or pasting already-bold text). Backed by a
 * contentEditable Tiptap editor rather than a plain `<textarea>`, which is
 * what unlocks both native Ctrl+B toggling and formatted paste retention —
 * a plain MUI `TextField` always strips formatting on paste and ignores Ctrl+B.
 */
const QuestionTextEditor: React.FC<QuestionTextEditorProps> = ({ label, value, onChange }) => {
  const editor = useEditor({
    extensions: questionEditorExtensions,
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !(value === "" && editor.isEmpty)) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <Box sx={{ flex: 2, minWidth: 220 }} onMouseDown={(e) => e.stopPropagation()}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25, ml: 0.25 }}>
        {label}
      </Typography>
      <Box
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          px: 1.5,
          py: 0.75,
          minHeight: 40,
          "&:hover": { borderColor: "rgba(0, 0, 0, 0.5)" },
          "&:focus-within": { borderColor: "primary.main", borderWidth: 2, px: "11px", py: "5px" },
          "& .ProseMirror": { outline: "none", fontSize: "0.95rem" },
          "& .ProseMirror p": { margin: 0 },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

/**
 * SurveyTemplatesPage — HR CRUD screen for Employee Survey templates.
 *
 * Lists existing templates in a table and opens a dialog to create/edit one:
 * a name, active flag, and a flat, re-orderable list of questions (Rating,
 * Single Choice, Checkbox, Yes/No, Text), each with its own required/optional
 * flag and (for choice-based types) an options list. Templates currently
 * assigned to an active survey cycle cannot be edited or deleted.
 */
const SurveyTemplatesPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const showToast = useCustomToast();

  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateListItem | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [questions, setQuestions] = useState<QuestionForm[]>([emptyQuestion(1)]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<TemplateListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  /** (Re)loads the full template list from the API. */
  const fetchTemplates = () => {
    setLoading(true);
    dispatch(fetchData(`${surveyTemplatesUrl}?page_size=100`))
      .then((res: any) => {
        if (res) setTemplates(res.results ?? res);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Opens the dialog pre-filled for a brand-new template (single blank question). */
  const openCreate = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setIsActive(true);
    setQuestions([emptyQuestion(1)]);
    setFormError("");
    setDialogOpen(true);
  };

  /** Opens the dialog pre-filled with an existing template's data, unless it's locked by an active cycle. */
  const openEdit = (tpl: TemplateListItem) => {
    if (tpl.active_cycle_count > 0) {
      showToast(
        `Cannot edit "${tpl.name}" — it is currently used in ${tpl.active_cycle_count} active cycle${tpl.active_cycle_count !== 1 ? "s" : ""}. Close those cycles first.`,
        "error",
        false
      );
      return;
    }
    setEditingTemplate(tpl);
    setTemplateName(tpl.name);
    setIsActive(tpl.is_active);
    const loaded: QuestionForm[] = (tpl.questions_read ?? []).map((q, qi) => ({
      question_id: q.question_id,
      question_text: q.question_text,
      question_type: q.question_type,
      rating_scale: q.rating_scale ?? (q.question_type === "rating" ? 5 : null),
      rating_style: q.rating_style ?? (q.question_type === "rating" ? "agreement" : null),
      is_mandatory: q.is_mandatory,
      order: q.order ?? qi + 1,
      options: (q.options ?? []).map((o, oi) => ({
        option_id: o.option_id,
        label: o.label,
        order: o.order ?? oi + 1,
      })),
    }));
    setQuestions(loaded.length > 0 ? loaded : [emptyQuestion(1)]);
    setFormError("");
    setDialogOpen(true);
  };

  /** Appends a new blank question to the builder form. */
  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion(prev.length + 1)]);

  /** Removes the question at index `qi` from the builder form. */
  const removeQuestion = (qi: number) =>
    setQuestions((prev) => prev.filter((_, i) => i !== qi));

  /** Updates a single, non-type field (text, mandatory flag, etc.) on the question at index `qi`. */
  const updateQuestion = (qi: number, field: keyof QuestionForm, value: any) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === qi ? { ...q, [field]: value } : q))
    );

  /**
   * Handles a change on the Type <Select>. `selectValue` is either one of
   * the `RATING_VARIANTS` keys (sets question_type="rating" plus that
   * variant's scale/style) or a plain non-rating QuestionType. Also seeds
   * two blank options when switching into a choice-based type that doesn't
   * have enough yet.
   */
  const updateQuestionType = (qi: number, selectValue: string) =>
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qi) return q;
        const variant = RATING_VARIANTS.find((v) => v.key === selectValue);
        if (variant) {
          return { ...q, question_type: "rating", rating_scale: variant.scale, rating_style: variant.style };
        }
        const t = selectValue as QuestionType;
        const updated: QuestionForm = { ...q, question_type: t, rating_scale: null, rating_style: null };
        if (needsOptions(t) && updated.options.length < 2) {
          updated.options = [emptyOption(1), emptyOption(2)];
        }
        return updated;
      })
    );

  /** Appends a new blank option to the question at index `qi`. */
  const addOption = (qi: number) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi ? { ...q, options: [...q.options, emptyOption(q.options.length + 1)] } : q
      )
    );

  /** Removes option `oi` from question `qi`. */
  const removeOption = (qi: number, oi: number) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi ? { ...q, options: q.options.filter((_, j) => j !== oi) } : q
      )
    );

  /** Updates the label of option `oi` on question `qi`. */
  const updateOption = (qi: number, oi: number, value: string) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi
          ? { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, label: value } : o)) }
          : q
      )
    );

  /** Validates the form, then creates or updates the template via the API. */
  const handleSave = async () => {
    if (!templateName.trim()) {
      setFormError("Template name is required.");
      return;
    }
    if (questions.some((q) => !stripHtml(q.question_text))) {
      setFormError("All question text fields are required.");
      return;
    }
    for (const q of questions) {
      if (needsOptions(q.question_type) && q.options.filter((o) => o.label.trim()).length < 2) {
        setFormError(`"${stripHtml(q.question_text)}" requires at least two options.`);
        return;
      }
    }

    setSaving(true);
    setFormError("");

    const payload = {
      name: templateName.trim(),
      is_active: isActive,
      questions: questions.map((q, qi) => ({
        ...(q.question_id ? { question_id: q.question_id } : {}),
        question_text: sanitizeBoldHtml(q.question_text),
        question_type: q.question_type,
        rating_scale: q.question_type === "rating" ? q.rating_scale ?? 5 : null,
        rating_style: q.question_type === "rating" ? q.rating_style ?? "agreement" : null,
        is_mandatory: q.is_mandatory,
        order: qi + 1,
        options: needsOptions(q.question_type)
          ? q.options
              .filter((o) => o.label.trim())
              .map((o, oi) => ({
                ...(o.option_id ? { option_id: o.option_id } : {}),
                label: o.label.trim(),
                order: oi + 1,
              }))
          : [],
      })),
    };

    try {
      if (editingTemplate) {
        await dispatch(putData(surveyTemplateDetailUrl(editingTemplate.template_id), payload));
        showToast("Template updated.", "success", false);
      } else {
        await dispatch(postData(surveyTemplatesUrl, payload));
        showToast("Template created.", "success", false);
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = data?.name?.[0] || data?.non_field_errors?.[0] || data?.detail;
      setFormError(detail ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /** Deletes the template selected via the confirmation dialog (`deleteTarget`). */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteData(surveyTemplateDetailUrl(deleteTarget.template_id)));
      showToast("Template deleted.", "success", false);
      setDeleteTarget(null);
      fetchTemplates();
    } catch {
      showToast("Failed to delete template.", "error", false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <PageTitle>Employee Survey Templates</PageTitle>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Template
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="info">
          No survey templates yet. Create one to define the questions employees will answer.
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: "#1E7B44",
                  "& .MuiTableCell-head": { color: "white", fontWeight: 700, fontSize: "0.82rem", py: 1.5 },
                }}
              >
                <TableCell>Template Name</TableCell>
                <TableCell>Questions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tpl, idx) => (
                <TableRow
                  key={tpl.template_id}
                  hover
                  sx={{
                    bgcolor: idx % 2 === 0 ? "white" : "grey.50",
                    borderLeft: 4,
                    borderColor: tpl.active_cycle_count > 0 ? "success.main" : "grey.300",
                  }}
                >
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body2" fontWeight="600">
                      {tpl.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Chip
                      label={`${tpl.questions_count} question${tpl.questions_count !== 1 ? "s" : ""}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Chip
                      label={tpl.is_active ? "Active" : "Inactive"}
                      size="small"
                      color={tpl.is_active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {tpl.created_by_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                    <Tooltip title={tpl.active_cycle_count > 0 ? "Cannot edit — in use by an active cycle" : "Edit template"}>
                      <span>
                        <IconButton size="small" onClick={() => openEdit(tpl)} disabled={tpl.active_cycle_count > 0}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={tpl.active_cycle_count > 0 ? "Cannot delete — in use by an active cycle" : "Delete template"}>
                      <span>
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(tpl)} disabled={tpl.active_cycle_count > 0}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh", maxHeight: "90vh" } }}
      >
        <DialogTitle>
          {editingTemplate ? `Edit: ${editingTemplate.name}` : "New Employee Survey Template"}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: "auto" }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Box display="flex" gap={2} alignItems="center" sx={{ mb: 3 }}>
            <TextField
              label="Template Name"
              fullWidth
              required
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label="Active"
              sx={{ whiteSpace: "nowrap" }}
            />
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight="medium">Questions</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addQuestion}>
              Add Question
            </Button>
          </Box>

          {questions.map((q, qi) => (
            <Paper
              key={qi}
              variant="outlined"
              draggable
              onDragStart={() => {
                setDragIdx(qi);
                setDragOverIdx(qi);
              }}
              onDragEnter={() => {
                if (dragIdx === null || dragIdx === qi) return;
                setDragOverIdx(qi);
                setQuestions((prev) => {
                  const next = [...prev];
                  const [moved] = next.splice(dragIdx, 1);
                  next.splice(qi, 0, moved);
                  return next;
                });
                setDragIdx(qi);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={() => {
                setDragIdx(null);
                setDragOverIdx(null);
              }}
              sx={{
                p: 2,
                mb: 1.5,
                opacity: dragIdx === qi ? 0.4 : 1,
                backgroundColor: dragOverIdx === qi && dragIdx !== qi ? "action.hover" : "inherit",
              }}
            >
              <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "grab",
                    "&:active": { cursor: "grabbing" },
                    color: "text.secondary",
                    mt: 3,
                  }}
                >
                  <DragIndicatorIcon fontSize="small" />
                </Box>
                <QuestionTextEditor
                  label={`Question ${qi + 1}`}
                  value={q.question_text}
                  onChange={(html) => updateQuestion(qi, "question_text", html)}
                />
                <FormControl size="small" sx={{ minWidth: 160 }} onMouseDown={(e) => e.stopPropagation()}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeSelectValue(q)}
                    label="Type"
                    onChange={(e) => updateQuestionType(qi, e.target.value)}
                  >
                    {RATING_VARIANTS.map((v) => (
                      <MenuItem key={v.key} value={v.key}>{v.label}</MenuItem>
                    ))}
                    {NON_RATING_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{NON_RATING_TYPE_LABELS[t]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  onMouseDown={(e) => e.stopPropagation()}
                  control={
                    <Switch
                      size="small"
                      checked={q.is_mandatory}
                      onChange={(e) => updateQuestion(qi, "is_mandatory", e.target.checked)}
                    />
                  }
                  label="Required"
                  sx={{ whiteSpace: "nowrap" }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeQuestion(qi)}
                  disabled={questions.length === 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {q.question_type === "rating" && (
                <Typography
                  variant="caption"
                  color="text.primary"
                  fontWeight={500}
                  sx={{ display: "block", pl: 1, mt: 1 }}
                >
                  {findRatingVariant(q.rating_scale, q.rating_style)?.helper ?? RATING_VARIANTS[0].helper}
                </Typography>
              )}

              {needsOptions(q.question_type) && (
                <Box sx={{ mt: 1.5, pl: 1 }} onMouseDown={(e) => e.stopPropagation()}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Options (min. 2)
                  </Typography>
                  {q.options.map((o, oi) => (
                    <Box key={oi} display="flex" gap={1} alignItems="center" sx={{ mt: 0.75 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder={`Option ${oi + 1}`}
                        value={o.label}
                        onChange={(e) => updateOption(qi, oi, e.target.value)}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeOption(qi, oi)}
                        disabled={q.options.length <= 2}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addOption(qi)} sx={{ mt: 0.75 }}>
                    Add Option
                  </Button>
                </Box>
              )}
            </Paper>
          ))}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={addQuestion}>
            Add Question
          </Button>
          <Box display="flex" gap={1}>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : editingTemplate ? "Save Changes" : "Create"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SurveyTemplatesPage;
